import React from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import MainLayout from "src/layouts/MainLayout/MainLayout";
import type { DtoAdminSearchBorrowerApplication } from "@porchpass/api-client";
import { applicationExternalId } from "src/api/preApproval/mappers";
import { PreApprovalEmptyState } from "../components/PreApprovalEmptyState";
import { WorkspaceProvider, useWorkspace } from "./WorkspaceContext";
import { WorkspaceHeader } from "./WorkspaceHeader";
import { SectionNav } from "./SectionNav";
import { PdfPreviewPanel } from "./PdfPreviewPanel";
import { SyncStatusBanner } from "./SyncStatusBanner";
import { SendPreApprovalModal } from "./send/SendPreApprovalModal";

function WorkspaceShell() {
  const { bootstrapState, bootstrapError, isPreviewMaximized } = useWorkspace();

  if (bootstrapState === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-sm text-[#697083]">
        Loading application...
      </div>
    );
  }

  if (bootstrapState === "error") {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <PreApprovalEmptyState
          title="Application unavailable"
          description={
            bootstrapError ??
            "This application could not be loaded. Return to search and try again."
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] min-h-0">
      <WorkspaceHeader />
      <SyncStatusBanner />
      <div className="flex flex-1 min-h-0">
        <SectionNav />
        {!isPreviewMaximized && (
          <div className="flex-1 min-w-0 min-h-0 flex flex-col">
            <Outlet />
          </div>
        )}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          <PdfPreviewPanel />
        </div>
      </div>
      <SendPreApprovalModal />
    </div>
  );
}

export function PreApprovalWorkspaceLayout() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const location = useLocation();

  if (!applicationId) {
    return <Navigate to="/pre-approval" replace />;
  }

  const state = location.state as {
    borrower?: DtoAdminSearchBorrowerApplication;
  } | null;
  const borrower =
    state?.borrower && applicationExternalId(state.borrower) === applicationId
      ? state.borrower
      : null;

  return (
    <MainLayout
      page="pre-approval"
      showHeading={false}
      isCollapsed={true}
      fullBleed={true}
    >
      <WorkspaceProvider externalId={applicationId} borrower={borrower}>
        <WorkspaceShell />
      </WorkspaceProvider>
    </MainLayout>
  );
}
